import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Search, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import type { AxiosInstance } from 'axios';
import { Floating } from '@/components/ui/Floating';
import { SelectField } from '@/components/ui/SelectField';

interface MainModule { id: string; name: string; scope?: string; }

interface Props {
  module?: any;
  onClose: () => void;
  onSaved: () => void;
  httpClient?: AxiosInstance;
  // When creating a new module, pre-select this scope (matches the
  // active tab on the listing page so the user does not have to set
  // it manually each time).
  defaultScope?: 'tenant' | 'platform';
}

const inputClass =
  'w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] transition-all';
const selectClass =
  'w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] transition-all appearance-none cursor-pointer';
const labelClass = 'block text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-1.5';

function SearchableParentSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (id: string) => void;
  options: MainModule[];
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.id === value);
  const filtered = options.filter(o => o.name.toLowerCase().includes(q.toLowerCase()));

  // Outside-click + Escape handled by <Floating> (below).

  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center justify-between w-full h-10 rounded-lg border px-3 text-sm transition-all ${
          open
            ? 'border-[#6366F1] ring-2 ring-[#6366F1]/30'
            : 'border-gray-200 hover:border-gray-300'
        } bg-white`}
      >
        <span className={selected ? 'text-[#1E293B]' : 'text-[#94A3B8]'}>
          {selected ? selected.name : 'Select parent module'}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#94A3B8] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      <Floating
        open={open}
        anchorRef={ref}
        onClose={() => setOpen(false)}
        className="rounded-lg border border-gray-100 bg-white shadow-lg overflow-hidden"
      >
          {/* Search */}
          <div className="p-2 border-b border-gray-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              <input
                ref={inputRef}
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search modules..."
                className="w-full h-8 pl-8 pr-3 rounded-md border border-gray-200 text-xs text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#6366F1]"
              />
            </div>
          </div>
          {/* Options */}
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-[#94A3B8]">No modules found</p>
            ) : filtered.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={`flex items-center w-full px-3 py-2 text-sm text-left transition-colors ${
                  o.id === value
                    ? 'bg-indigo-50 text-[#6366F1] font-medium'
                    : 'text-[#1E293B] hover:bg-gray-50'
                }`}
              >
                {o.name}
              </button>
            ))}
          </div>
      </Floating>
    </div>
  );
}

export function ModuleFormDrawer({ module, onClose, onSaved, httpClient = api, defaultScope }: Props) {
  const isEdit = !!module;
  const [mainModules, setMainModules] = useState<MainModule[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: module?.name || '',
    pageLink: module?.pageLink || '',
    icon: module?.icon || '',
    type: module?.type || 'M',
    scope: module?.scope || defaultScope || 'tenant',
    parentId: module?.parentId || '',
    orderNumber: module?.orderNumber || 1,
    isActive: module?.isActive ?? true,
  });

  // Re-fetch parent candidates whenever the chosen scope changes so a
  // tenant sub-module can only have a tenant parent, and vice versa.
  useEffect(() => {
    httpClient
      .get('/admin/modules', { params: { type: 'M', scope: form.scope } })
      .then(r => setMainModules(r.data))
      .catch(() => {});
  }, [form.scope]);

  const set = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        parentId: form.type === 'S' ? (form.parentId || undefined) : undefined,
        orderNumber: Number(form.orderNumber),
      };
      if (isEdit) {
        await httpClient.patch(`/admin/modules/${module.id}`, payload);
      } else {
        await httpClient.post('/admin/modules', payload);
      }
      onSaved();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save module');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full sm:w-[600px] sm:max-w-none flex-shrink-0 bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-[#1E293B]">{isEdit ? 'Edit Module' : 'Add Module'}</h2>
            <p className="text-xs text-[#94A3B8] mt-0.5">{isEdit ? 'Update module details' : 'Create a new system module'}</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#64748B] transition-colors"
          >
            <X className="h-4 w-4" /> Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Row 1: Module Name (full width) */}
          <div>
            <label className={labelClass}>Module Name <span className="text-red-500 normal-case">*</span></label>
            <input
              className={inputClass}
              placeholder="e.g. Lead Management"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              required
            />
          </div>

          {/* Row 2: Page Link (full width) */}
          <div>
            <label className={labelClass}>Page Link <span className="text-red-500 normal-case">*</span></label>
            <input
              className={inputClass}
              placeholder="e.g. /leads, /users, #"
              value={form.pageLink}
              onChange={e => set('pageLink', e.target.value)}
              required
            />
          </div>

          {/* Row 3: Module Icon | Module Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Module Icon</label>
              <input
                className={inputClass}
                placeholder="e.g. fa fa-cogs"
                value={form.icon}
                onChange={e => set('icon', e.target.value)}
              />
              {form.icon && (
                <div className="mt-2 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
                  <div className="h-6 w-6 rounded-md bg-white border border-indigo-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${form.icon} text-[#6366F1] text-xs`} />
                  </div>
                  <span className="text-xs text-[#6366F1] font-medium">Preview</span>
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Module Type <span className="text-red-500 normal-case">*</span></label>
              <div className="relative">
                <SelectField
                  className={selectClass}
                  value={form.type}
                  onChange={e => {
                    set('type', e.target.value);
                    if (e.target.value === 'M') set('parentId', '');
                  }}
                  required
                >
                  <option value="M">M — Main Module</option>
                  <option value="S">S — Sub Module</option>
                </SelectField>
              </div>
            </div>
          </div>

          {/* Row 3b: Scope (vendor vs super-admin) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Scope <span className="text-red-500 normal-case">*</span></label>
              <div className="relative">
                <SelectField
                  className={selectClass}
                  value={form.scope}
                  onChange={e => {
                    set('scope', e.target.value);
                    // Reset parent when scope changes — old parent may
                    // belong to the other scope.
                    set('parentId', '');
                  }}
                  required
                >
                  <option value="tenant">Vendor (Tenant App)</option>
                  <option value="platform">Super-admin (Platform)</option>
                </SelectField>
              </div>
            </div>
          </div>

          {/* Row 4: Parent Module | Order Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Parent Module
                {form.type === 'S' && <span className="text-red-500 normal-case"> *</span>}
              </label>
              {form.type === 'S' ? (
                <>
                  <SearchableParentSelect
                    value={form.parentId}
                    onChange={id => set('parentId', id)}
                    options={mainModules.filter(m => !isEdit || m.id !== module?.id)}
                  />
                  {!form.parentId && (
                    <p className="mt-1 text-xs text-[#94A3B8]">Required for sub-modules</p>
                  )}
                </>
              ) : (
                <div className="h-10 rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center px-3">
                  <span className="text-xs text-[#CBD5E1]">N/A — Main module</span>
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Order Number <span className="text-red-500 normal-case">*</span></label>
              <input
                className={inputClass}
                type="number"
                min={1}
                placeholder="1"
                value={form.orderNumber}
                onChange={e => set('orderNumber', parseInt(e.target.value) || 1)}
                required
              />
            </div>
          </div>

          {/* Row 5: Status (half width) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Status</label>
              <div className="relative">
                <SelectField
                  className={selectClass}
                  value={form.isActive ? 'active' : 'inactive'}
                  onChange={e => set('isActive', e.target.value === 'active')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </SelectField>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60 transition-colors"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Module'}
          </button>
        </div>
      </div>
    </div>
  );
}
